/**
 * The users record as this portal reads it, and the membership check.
 *
 * Everything here is a deliberate port of `account/MembershipGate.kt` and
 * `account/TorahlyAccountClient.kt` in the Android app. The two implementations answer the
 * same question about the same record, so they have to agree. Where the Kotlin makes an
 * unobvious choice, the choice is repeated here with its reason, not silently improved.
 */

export interface CovellaUser {
  id: string
  email: string
  name: string
  role: string[] | string | null
  gender?: string
  created: string
  covella_member?: boolean
  covella_member_until?: string
}

/**
 * Every role the account holds.
 *
 * `role` on the Torahly users record is a multi-select, so a woman can be an elder's wife and
 * a moderator at once. Permission questions must ask this rather than the first entry. Accounts
 * written before the normalising migration still carry a bare string, hence the fallback.
 */
export function readRoles(user: Pick<CovellaUser, 'role'>): string[] {
  const raw = user.role
  if (Array.isArray(raw)) {
    const values = raw.map((r) => String(r).trim()).filter((r) => r.length > 0)
    return values.length > 0 ? values : ['member']
  }
  const single = typeof raw === 'string' ? raw.trim() : ''
  return [single.length > 0 ? single : 'member']
}

/**
 * True when the account's role is stored as a bare string rather than as a list.
 *
 * `readRoles` tolerates that shape so the panel opens; the SERVER does not. Every gated
 * collection is written `@request.auth.role ?= "..."` and `?=` matches only the array form, so
 * this account reads every queue and is refused on every write, with the rule text correct and
 * the role showing correctly in the admin UI. It is the one failure the panel can detect on its
 * own, from the auth record it already holds, before a single save is attempted.
 *
 * Repaired by re-saving the role on the account, or by re-running
 * `Torahly/backend/pb_migrations/1753660000_normalize_user_roles.js`, which exists for this.
 */
export function roleStoredAsBareString(user: Pick<CovellaUser, 'role'>): boolean {
  return typeof user.role === 'string' && user.role.trim().length > 0
}

/** First role, for display only. */
export function readRole(user: Pick<CovellaUser, 'role'>): string {
  return readRoles(user)[0] ?? 'member'
}

/** PocketBase datetimes arrive as "2026-09-03 12:00:00.000Z". */
export function parsePocketBaseInstant(raw: string): Date | null {
  const trimmed = raw.trim()
  if (trimmed.length === 0) return null
  // Kotlin's String.replace swaps every occurrence, so the regex here is not optional:
  // JS replace(' ', 'T') would only take the first and leave the rest unparseable.
  const withT = trimmed.replace(/ /g, 'T')
  const normalized = withT.endsWith('Z') ? withT : withT + 'Z'
  const parsed = new Date(normalized)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/**
 * True only for a user whose server-controlled `covella_member` flag is set **and** whose paid
 * period has not run out.
 *
 * The date check is what lets a subscription lapse. Trusting the flag alone would mean a
 * cancellation whose webhook never arrived, because the server was down or Stripe exhausted
 * its retries, granted permanent access with nothing to catch it.
 *
 * Blank `covella_member_until` means no expiry, which is how a lifetime purchase or a comped
 * account reads. An unparseable date is treated as no expiry rather than as expired, on
 * purpose: a formatting change on the server must not lock paying members out of what they
 * bought.
 *
 * Note what this is not. It is a display check, and nothing more. On the web the client is
 * fully inspectable, so anything actually worth money has to be protected by a PocketBase API
 * rule on the collection holding it. See PORTAL.md section 5.
 */
export function isMember(user: CovellaUser | null | undefined): boolean {
  if (!user) return false
  if (!user.covella_member) return false
  const until = (user.covella_member_until ?? '').trim()
  if (until.length === 0) return true
  const expiry = parsePocketBaseInstant(until)
  if (!expiry) return true
  return Date.now() < expiry.getTime()
}

export function formatDate(raw: string): string | null {
  const parsed = parsePocketBaseInstant(raw)
  if (!parsed) return null
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}
